import { useState, useEffect } from 'react';
import { firestoreService } from '../firestoreService';
import { useSchool } from '../contexts/SchoolContext';

// School-scoped collections that require schoolId
const SCHOOL_SCOPED_COLLECTIONS = [
  'students', 'classes', 'mentions', 'offences', 'values',
  'disciplineRules', 'queue', 'disciplineQueue', 'seatingPlans', 'smartLists',
  'consequenceTypes', 'disciplineCategories', 'menuItems',
  'thresholdRules', 'escalationRules', 'mentionRules', 'zeroToleranceRules', 'accountabilityRules',
  'escalationTracking', 'ruleApplications', 'accountabilityPeriods',
  'behaviorContracts', 'studentContracts', 'dailyStats',
  'subjects',
  'timetableAssignments',
  'hierarchies',
  'familyCards',
];

// SessionStorage cache configuration
// No TTL - real-time listener handles data freshness
// Cache persists until tab closes or listener updates it

/**
 * Generate cache key for a collection with conditions
 * @param {string} collectionName - Collection name
 * @param {string} schoolId - School ID
 * @param {Array} conditions - Query conditions
 * @returns {string} Cache key
 */
const getCacheKey = (collectionName, schoolId, conditions = []) => {
  const conditionsKey = conditions.length > 0
    ? '_' + JSON.stringify(conditions).replace(/[^a-zA-Z0-9]/g, '')
    : '';
  return `swan_realtime_${collectionName}_${schoolId || 'global'}${conditionsKey}`;
};

/**
 * Save data to sessionStorage with timestamp
 * @param {string} cacheKey - Cache key
 * @param {Array} data - Data to cache
 */
const saveToSessionStorage = (cacheKey, data) => {
  try {
    const cacheEntry = {
      data,
      timestamp: Date.now()
    };
    sessionStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
    console.log(`[useRealtimeData] 💾 Saved to cache: ${cacheKey} (${data.length} items)`);
  } catch (error) {
    // Handle quota exceeded or other errors - fail silently
    if (error.name === 'QuotaExceededError') {
      console.warn('[useRealtimeData] sessionStorage quota exceeded');
    }
  }
};

/**
 * Load data from sessionStorage
 * Real-time listener handles data freshness - no TTL needed
 * @param {string} cacheKey - Cache key
 * @returns {Array|null} Cached data or null if not found
 */
const loadFromSessionStorage = (cacheKey) => {
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (!cached) return null;

    const cacheEntry = JSON.parse(cached);

    // No expiration check - real-time listener keeps data fresh
    const cacheAge = Date.now() - cacheEntry.timestamp;
    console.log(`[useRealtimeData] 📦 CACHE HIT: ${cacheKey}`, {
      cacheAgeSec: (cacheAge / 1000).toFixed(1),
      itemCount: cacheEntry.data.length
    });
    return cacheEntry.data;
  } catch (error) {
    // Handle invalid JSON - clear corrupted cache
    try {
      sessionStorage.removeItem(cacheKey);
    } catch (e) {
      // Ignore cleanup errors
    }
    return null;
  }
};

export const useFirebaseData = (collectionName, conditions = []) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { schoolId, loading: schoolLoading } = useSchool();

  useEffect(() => {
    const fetchData = async () => {
      // Check if this is a school-scoped collection
      const isSchoolScoped = SCHOOL_SCOPED_COLLECTIONS.includes(collectionName);

      console.log(`[useFirebaseData:${collectionName}] Effect running:`, { isSchoolScoped, schoolLoading, schoolId });

      // Wait for school context to load before querying school-scoped collections
      if (isSchoolScoped && schoolLoading) {
        console.log(`[useFirebaseData:${collectionName}] School still loading, skipping`);
        return;
      }

      // CRITICAL: Never proceed with school-scoped queries without a valid schoolId
      if (isSchoolScoped && !schoolId) {
        console.log(`[useFirebaseData:${collectionName}] No schoolId, returning empty`);
        setData([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Auto-add schoolId filter for school-scoped collections
        const finalConditions = isSchoolScoped && schoolId
          ? [...conditions, ['schoolId', '==', schoolId]]
          : conditions;

        let result;
        if (finalConditions.length > 0) {
          result = await firestoreService.query(collectionName, finalConditions);
        } else {
          result = await firestoreService.getAll(collectionName);
        }

        setData(result);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [collectionName, schoolId, schoolLoading, JSON.stringify(conditions)]);

  const create = async (newData) => {
    try {
      // Auto-inject schoolId for school-scoped collections
      const dataWithSchool = SCHOOL_SCOPED_COLLECTIONS.includes(collectionName)
        ? { ...newData, schoolId }
        : newData;

      const id = await firestoreService.create(collectionName, dataWithSchool);
      const createdItem = { id, ...dataWithSchool, createdAt: new Date(), updatedAt: new Date() };
      setData(prevData => [...prevData, createdItem]);
      return createdItem;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const update = async (id, updatedData) => {
    try {
      // Auto-inject schoolId for school-scoped collections
      const dataWithSchool = SCHOOL_SCOPED_COLLECTIONS.includes(collectionName)
        ? { ...updatedData, schoolId }
        : updatedData;

      await firestoreService.update(collectionName, id, dataWithSchool);
      setData(prevData =>
        prevData.map(item =>
          item.id === id
            ? { ...item, ...dataWithSchool, updatedAt: new Date() }
            : item
        )
      );
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const remove = async (id) => {
    try {
      await firestoreService.delete(collectionName, id);
      setData(prevData => prevData.filter(item => item.id !== id));
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  return {
    data,
    loading,
    error,
    create,
    update,
    remove,
    refetch: () => {
      setLoading(true);
      setError(null);
    }
  };
};

export const useRealtimeData = (collectionName, conditions = []) => {
  const { schoolId, loading: schoolLoading } = useSchool();

  // Generate cache key (needs to be done before useState to be consistent)
  const cacheKey = getCacheKey(collectionName, schoolId, conditions);

  // Load from cache SYNCHRONOUSLY before first render (prevents flash)
  const [data, setData] = useState(() => {
    const cached = loadFromSessionStorage(cacheKey);
    return cached || [];
  });

  // If we have cached data, we're not loading
  const [loading, setLoading] = useState(() => {
    const cached = loadFromSessionStorage(cacheKey);
    return !cached || cached.length === 0;
  });

  const [error] = useState(null); // setError removed as it's not used in realtime hook

  useEffect(() => {
    // Check if this is a school-scoped collection
    const isSchoolScoped = SCHOOL_SCOPED_COLLECTIONS.includes(collectionName);

    console.log(`[useRealtimeData:${collectionName}] Effect running:`, { isSchoolScoped, schoolLoading, schoolId });

    // Wait for school context to load before subscribing to school-scoped collections
    if (isSchoolScoped && schoolLoading) {
      console.log(`[useRealtimeData:${collectionName}] School still loading, skipping`);
      return;
    }

    // CRITICAL: Never proceed with school-scoped subscriptions without a valid schoolId
    if (isSchoolScoped && !schoolId) {
      console.log(`[useRealtimeData:${collectionName}] No schoolId, returning empty`);
      setData([]);
      setLoading(false);
      return;
    }

    // Use hierarchical paths for school-scoped collections (match useAdminData)
    const actualCollectionPath = isSchoolScoped && schoolId
      ? `schools/${schoolId}/${collectionName}`  // Hierarchical like "schools/abc123/students"
      : collectionName;                          // Flat for global collections

    // Phase 2: Add timeout for initial snapshot
    let listenerTimeout;
    
    // Set timeout for initial snapshot to prevent infinite loading
    if (loading) {
      listenerTimeout = setTimeout(() => {
        console.warn(`[useRealtimeData] ⚠️ TIMEOUT: No data received for ${collectionName} - forcing completion`);
        setLoading(false); // Force loading to complete
        setData([]); // Ensure we have empty data instead of undefined
      }, 15000);
    }

    // No need for schoolId filter with hierarchical paths
    const unsubscribe = firestoreService.subscribe(
      actualCollectionPath,
      (newData, metadata) => {
        // Clear timeout on first successful data callback
        if (listenerTimeout) {
          clearTimeout(listenerTimeout);
          listenerTimeout = null;
        }
        
        // Only cache data from server, not from Firestore's local cache
        // This prevents the initial cached/empty snapshot from overwriting valid sessionStorage
        if (metadata && !metadata.fromCache) {
          saveToSessionStorage(cacheKey, newData);
        }

        setData(newData);
        setLoading(false);
      },
      conditions
    );

    return () => {
      if (listenerTimeout) {
        clearTimeout(listenerTimeout);
      }
      unsubscribe();
    };
  }, [collectionName, schoolId, schoolLoading, cacheKey, JSON.stringify(conditions)]);

  return { data, loading, error };
};